import React, { useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";

const DualRowItem = ({ leftChild, rightChild }) => {
  return (
    <div
      className="flex 
                 flex-col sm:flex-col lg:flex-row
                 w-full
                "
    >
      {leftChild}
      <div className="px-0 lg:px-5" />
      {rightChild}
    </div>
  );
};

const RegularField = ({
  value,
  setFunction,
  fieldLabel,
  placeholderText,
  fieldType = "text",
  fieldWarning,
  takeFullWidth = false,
  fieldDisabled = false,
  customOnChange = false,
}) => {
  const handleInputChange = (event) => {
    setFunction(event.target.value);
  };

  return (
    <div
      className={
        "flex flex-col mt-6 " + (takeFullWidth ? "w-full" : "w-full sm:w-4/5")
      }
    >
      <div
        className={`text-base font-light ml-1 mb-1 text-neutral-600  ${
          fieldDisabled ? "opacity-50" : ""
        } `}
      >
        {fieldLabel}
      </div>

      <div className="flex flex-grow items-center justify-between w-full">
        <input
          className={`flex-grow-9 flex flex-grow items-center pl-2 h-10 border border-neutral-300 rounded-lg
              focus:outline-none focus:ring-inset focus:ring-neutral-200 
              ${fieldWarning ? "bg-rose-100" : ""} 
              ${fieldDisabled ? "opacity-50" : ""} 
              
              `}
          type={fieldType}
          onChange={customOnChange ? customOnChange : handleInputChange}
          value={value}
          placeholder={placeholderText}
          disabled={fieldDisabled}
        ></input>
      </div>
    </div>
  );
};

const SelectField = ({
  fieldLabel,
  placeholderText,
  setFunction,
  value,
  fieldWarning,
  takeFullWidth = false,
  fieldDisabled = false,
  options,
}) => {
  const handleSelectChange = (event) => {
    setFunction(event.target.value);
  };

  return (
    <div
      className={
        "flex flex-col mt-6 " + (takeFullWidth ? "w-full" : "w-full sm:w-4/5")
      }
    >
      <div
        className={`text-base font-light ml-1 mb-1 text-neutral-600 ${
          fieldDisabled ? "opacity-50" : ""
        } `}
      >
        {fieldLabel}
      </div>

      <div className="flex flex-grow items-center justify-between w-full">
        <select
          className={`flex-grow-9 flex flex-grow items-center h-10 border border-neutral-300 rounded-lg
            focus:outline-none focus:ring-inset focus:ring-neutral-200
            ${fieldWarning ? "bg-rose-100" : ""}
            ${fieldDisabled ? "opacity-50" : ""} 
            `}
          onChange={handleSelectChange}
          value={value}
          disabled={fieldDisabled}
        >
          <option disabled value="">
            {placeholderText}
          </option>
          {options.map((option, index) => (
            <option key={`${uuidV4()}-${index}`} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

const PasswordField = ({
  passwordLabel,
  placeholderText,
  setFunction,
  value,
  passwordWarning,
  takeFullWidth = false,
}) => {
  const [passwordHidden, setPasswordHidden] = useState(true);

  const handleInputChange = (event) => {
    setFunction(event.target.value);
  };

  return (
    <div
      className={
        "flex flex-col mt-6 " + (takeFullWidth ? "w-full" : "w-full sm:w-4/5")
      }
    >
      <div className="text-base font-light ml-1 mb-1 text-neutral-600">
        {passwordLabel}
      </div>

      <div className="flex flex-grow items-center justify-between w-full">
        <input
          className={`flex-grow-9 flex flex-grow items-center pl-2 h-10 border border-neutral-300 rounded-l-lg
              focus:outline-none focus:ring-inset focus:ring-neutral-200
              ${passwordWarning ? "bg-rose-100" : ""}
              `}
          type={passwordHidden ? "password" : "text"}
          onChange={handleInputChange}
          value={value}
          placeholder={placeholderText}
        ></input>
        <button
          className="flex-grow-1 flex items-center justify-center h-10 w-10 rounded-r-lg
              bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300"
          onClick={() => {
            setPasswordHidden(!passwordHidden);
          }}
        >
          {passwordHidden ? (
            <EyeSlashIcon className="h-6 w-6 flex-none text-neutral-950" />
          ) : (
            <EyeIcon className="h-6 w-6 flex-none text-neutral-950" />
          )}
        </button>
      </div>
    </div>
  );
};

const EntryRegularField = ({
  fieldValue,
  setFieldValue,
  placeholder,
  fieldWarning,
  maxLength,
  inputType = "text",
}) => {
  const handleChange = (event) => {
    setFieldValue(event.target.value);
  };

  return (
    <div className="flex flex-col w-full">
      <input
        className={`flex w-full items-center p-2 border border-neutral-300 rounded-lg
        focus:outline-none focus:ring-inset focus:ring-neutral-300
        ${fieldWarning ? "bg-rose-100" : ""}
        `}
        placeholder={placeholder}
        value={fieldValue}
        onChange={handleChange}
        maxLength={maxLength}
        type={inputType}
      />
    </div>
  );
};

const EntryTextAreaField = ({
  fieldValue,
  setFieldValue,
  placeholder,
  fieldWarning,
  rowNumber,
  maxLength,
}) => {
  const handleChange = (event) => {
    setFieldValue(event.target.value);
  };

  return (
    <div className="flex flex-col w-full">
      <textarea
        className={`resize-y flex w-full items-center p-2 h-min-10 border border-neutral-300 rounded-lg
        focus:outline-none focus:ring-inset focus:ring-neutral-300
        ${fieldWarning ? "bg-rose-100" : ""}
        `}
        placeholder={placeholder}
        value={fieldValue}
        onChange={handleChange}
        rows={rowNumber}
        maxLength={maxLength}
      />
    </div>
  );
};

export {
  DualRowItem,
  RegularField,
  SelectField,
  PasswordField,
  EntryRegularField,
  EntryTextAreaField,
};
